import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import Individual from "marketplace/individuals/individual";
import UserInformationSchema, {
  UserInformationJSON,
  UserInformationParams,
} from "users/information/_schemas/user-information-schema";

export default class UserInformation {
  public static readonly EXAMPLE: UserInformation = UserInformation.create({
    id: Guid.generate(),
    isVendor: false,
    isClient: true,
    isVendorRepresentative: false,
    isClientRepresentative: true,
    viewingAsVendor: false,
    email: EmailAddress.create("test@example.com"),
    name: Name.create({ params: { firstName: "Test", lastName: "User" } }),
    legalEntityId: Guid.generate(),
    openEntityAgreements: [UserOpenEntityAgreement.TEST_AGREEMENT],
    hasOpenEntityAgreements: false,
    isSuperUser: false,
    avatarId: Guid.generate(),
    companyId: Guid.generate(),
    companyName: "Example Company",
    companyProfileVisible: true,
    teamsOn: 3,
    teamsLeading: 1,
    myComments: "This is a test user.",
    onboardingWorkId: Guid.generate(),
    connectionId: Guid.generate(),
    createdDate: new AHDate(),
    individualId: Guid.generate(),
  });

  public static create(values: UserInformationParams): UserInformation {
    const parsed = UserInformationSchema.validate(values);
    return new UserInformation(parsed);
  }

  public static fromJSON(json: UserInformationJSON | null | undefined): UserInformation | undefined {
    if (isNullOrUndefined(json)) return undefined;
    const params = UserInformationSchema.deserialize(json);
    return new UserInformation(params);
  }

  public static fromJSONArray(jsonArray: (UserInformationJSON | null | undefined)[]): UserInformation[] {
    if (isNullOrUndefined(jsonArray)) return [];
    return jsonArray
      .map((json) => UserInformation.fromJSON(json))
      .filter((user): user is UserInformation => !isNullOrUndefined(user));
  }

  public readonly id: Guid;
  public readonly isSuperUser: boolean;
  public readonly isVendor: boolean;
  public readonly isClient: boolean;
  public readonly isVendorRepresentative: boolean;
  public readonly isClientRepresentative: boolean;
  public readonly viewingAsVendor: boolean;
  public readonly email?: EmailAddress;
  public readonly name: Name;
  public readonly openEntityAgreements: UserOpenEntityAgreement[];
  public readonly hasOpenEntityAgreements: boolean;
  public readonly individualId?: Guid;
  public readonly connectionId?: Guid;
  public readonly createdDate?: AHDate;
  public readonly companyId?: Guid;
  public readonly legalEntityId: Guid;
  public readonly companyName?: string;
  public readonly companyProfileVisible?: boolean;
  public readonly avatarId?: Guid;
  public readonly teamsOn?: number;
  public readonly teamsLeading?: number;
  public readonly myComments?: string;
  public readonly onboardingWorkId?: Guid;

  public constructor(params: UserInformationParams) {
    this.id = params.id;
    this.isVendor = params.isVendor;
    this.isClient = params.isClient;
    this.isVendorRepresentative = params.isVendorRepresentative;
    this.isClientRepresentative = params.isClientRepresentative;
    this.viewingAsVendor = params.viewingAsVendor;
    this.email = params.email;
    this.name = params.name;
    this.legalEntityId = params.legalEntityId;
    this.openEntityAgreements = this.mapOpenEntityAgreements(params.openEntityAgreements);
    this.hasOpenEntityAgreements = params.hasOpenEntityAgreements ?? false;
    this.individualId = params.individualId;
    this.isSuperUser = params.isSuperUser;
    this.connectionId = params.connectionId;
    this.createdDate = params.createdDate;
    this.companyId = params.companyId;
    this.companyName = params.companyName ?? undefined;
    this.companyProfileVisible = params.companyProfileVisible;
    this.avatarId = params.avatarId;
    this.teamsOn = params.teamsOn;
    this.teamsLeading = params.teamsLeading;
    this.myComments = params.myComments;
    this.onboardingWorkId = params.onboardingWorkId;
  }

  private mapOpenEntityAgreements(
    agreements?: UserOpenEntityAgreement[]
  ): UserOpenEntityAgreement[] {
    if (!agreements) return [];
    return agreements.map((agreement) =>
      UserOpenEntityAgreement.create({
        id: agreement.id,
        entityName: agreement.entityName,
      })
    );
  }

  public toJSON(): UserInformationJSON {
    return UserInformationSchema.serialize(this);
  }

  public isEqualTo(other: UserInformation): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toIndividual(): Individual | undefined {
    if (isNullOrUndefined(this.individualId)) {
      console.warn("UserInformation.toIndividual: individualId is null or undefined.");
      return;
    }
    if (isNullOrUndefined(this.legalEntityId)) {
      console.warn("UserInformation.toIndividual: legalEntityId is null or undefined.");
      return;
    }
    if (isNullOrUndefined(this.name.firstName) || isNullOrUndefined(this.name.lastName)) {
      console.warn("UserInformation.toIndividual: name is incomplete.");
      return;
    }

    return Individual.create({
      ...this,
      id: this.individualId,
      userId: this.id,
      isVisible: true,
      legalEntityId: this.legalEntityId,
      profile: {
        firstName: this.name.firstName,
        lastName: this.name.lastName,
        email: this.email,
      },
    });
  }

  public toString(): string {
    return `UserInformation (
      id: ${this.id.value},
      isVendor: ${this.isVendor},
      isClient: ${this.isClient},
      isVendorRepresentative: ${this.isVendorRepresentative},
      isClientRepresentative: ${this.isClientRepresentative},
      viewingAsVendor: ${this.viewingAsVendor},
      email: ${this.email?.toString()},
      name: ${this.name.toString()},
      openEntityAgreements: [${this.openEntityAgreements.map((a) => a.toString()).join(", ")}],
      hasOpenEntityAgreements: ${this.hasOpenEntityAgreements},
      individualId: ${this.individualId},
      isSuperUser: ${this.isSuperUser},
      connectionId: ${this.connectionId},
      createdDate: ${this.createdDate},
      companyId: ${this.companyId},
      companyName: ${this.companyName},
      companyProfileVisible: ${this.companyProfileVisible},
      avatarId: ${this.avatarId},
      teamsOn: ${this.teamsOn},
      teamsLeading: ${this.teamsLeading},
      myComments: ${this.myComments},
      onboardingWorkId: ${this.onboardingWorkId}
    )`;
  }
}
